/**
 * Lead Source Select
 * Hierarchical lead source picker. Options are grouped by top-level source
 * and labelled with the full breadcrumb ("Online > Google > Google Ads").
 *
 * Inactive sources are hidden, except the record's current source (so saving
 * unrelated edits does not silently clear it; the backend allows keeping an
 * inactive source but not newly assigning one).
 *
 * Loads the catalog itself unless `sources` is passed in.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createAPIClient, ILeadSourcesApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { treeOrder } from '../utils/leadSource';
import { inputClass } from './formStyles';
import { Toast } from './Toast';

interface LeadSourceSelectProps {
  id: string;
  /** Selected lead source ID, or null for none. */
  value: string | null;
  onChange: (leadSourceId: string | null) => void;
  disabled?: boolean;
  /** The record's saved source; kept selectable even if it is now inactive. */
  currentId?: string | null;
  /** Pre-loaded catalog; when omitted the component fetches it. */
  sources?: types.LeadSource[];
  className?: string;
}

interface OptionGroup {
  root: types.LeadSource;
  options: types.LeadSource[];
}

export function LeadSourceSelect({
  id,
  value,
  onChange,
  disabled,
  currentId,
  sources: providedSources,
  className,
}: LeadSourceSelectProps) {
  const apiClient = useMemo<ILeadSourcesApi>(() => createAPIClient(), []);
  const [loadedSources, setLoadedSources] = useState<types.LeadSource[]>([]);
  const [isLoading, setIsLoading] = useState(!providedSources);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (providedSources) return;
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const list = await apiClient.listLeadSources();
        if (!cancelled) setLoadedSources(list);
      } catch (err) {
        if (!cancelled) setLoadError(getErrorMessage(err, 'Failed to load lead sources'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [apiClient, providedSources]);

  const sources = providedSources ?? loadedSources;

  // Group the visible options under their top-level source, in tree order.
  const groups = useMemo<OptionGroup[]>(() => {
    const keep = (s: types.LeadSource) => s.active || s.id === currentId || s.id === value;
    const result: OptionGroup[] = [];
    for (const source of treeOrder(sources)) {
      if (source.depth === 0 || result.length === 0) {
        result.push({ root: source, options: [] });
      }
      if (keep(source)) result[result.length - 1].options.push(source);
    }
    return result.filter((g) => g.options.length > 0);
  }, [sources, currentId, value]);

  const hasOptions = groups.length > 0;

  return (
    <div className={className}>
      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled || isLoading}
        className={inputClass}
      >
        <option value="">{isLoading ? 'Loading lead sources...' : 'None'}</option>
        {groups.map((group) => (
          <optgroup key={group.root.id} label={group.root.name}>
            {group.options.map((source) => (
              <option key={source.id} value={source.id}>
                {source.pathLabel}
                {!source.active ? ' (inactive)' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {loadError && <Toast kind="error">{loadError}</Toast>}
      {!loadError && !isLoading && !hasOptions && (
        <p className="mt-1.5 text-xs text-gray-500">No active lead sources have been set up yet.</p>
      )}
    </div>
  );
}
